import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import { setActiveUsers } from '../reducers/activeUsers';
import { setMemoryUsage } from '../reducers/memory/memoryUsage';

export const useSocket = (user) => {
  const [socket, setSocket] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();

  // Initialize socket connection
  useEffect(() => {
    const socketInstance = io(window.location.origin, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity, // keep trying forever
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000 
    });

    socketInstance.on('activeUsers', (data) => {
      // console.log('Client received activeUsers data:', data);
      dispatch(setActiveUsers(data));
    });

    socketInstance.on('memoryUsage', (data) => {
      dispatch(setMemoryUsage(data));
    });

    const handleBeforeUnload = () => {
      if (socketInstance) {
        socketInstance.disconnect();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    setSocket(socketInstance);

    return () => {
      if (socketInstance) {
        socketInstance.disconnect();
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dispatch]);

  // Send authenticate when user is available
  useEffect(() => {
    if (socket && user) {
      socket.emit('authenticate', {
        userId: user.id,
        user: user,
        currentPage: location.pathname
      });
    }
  }, [socket, user, location.pathname]);



  // Send page changes
  useEffect(() => {
    if (socket && user) {
      socket.emit('pageChange', {
        userId: user.id,
        user: user,
        page: location.pathname
      });
    }
  }, [location.pathname, socket, user]);

  

  // Emit socket event when app is not visible (tab inactive or minimized)
  useEffect(() => {
    if (!socket || !user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        socket.emit('appNotVisible', {
          userId: user.id,
          user: user,
          page: location.pathname,
          isVisible: false
        });
      } else if (document.visibilityState === 'visible') {
        socket.emit('appVisible', {
          userId: user.id,
          user: user,
          page: location.pathname,
          isVisible: true
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [socket, user, location.pathname]);

  return socket;
};
